import { useEffect, useRef } from "react";
import { Link, NavLink, Route, Routes, useSearchParams } from "react-router-dom";

import { checkMessageThread, getMe, saveFavoriteFromSdk } from "@/api/favorites";
import { toast, ToastLayer } from "@/components/Toast";
import {
  getBlockActionSourceDetail,
  getTriggerCodeFromUrl,
  getTriggerContext,
  initH5Sdk,
  detectEntryScenario,
  type EntryScenario,
  type CurrentChatInfo,
} from "@/feishu/jssdk";
import AdminLayout from "@/pages/admin/AdminLayout";
import AuditPage from "@/pages/admin/AuditPage";
import HealthPage from "@/pages/admin/HealthPage";
import StatsPage from "@/pages/admin/StatsPage";
import UserDetailPage from "@/pages/admin/UserDetailPage";
import UsersPage from "@/pages/admin/UsersPage";
import FavoritesPage from "@/pages/FavoritesPage";
import GroupsPage from "@/pages/GroupsPage";
import { useAppStore } from "@/store/auth";

/** 统一入口管理：根据入口场景分发处理逻辑。 */
function EntryHandler() {
  const [searchParams, setSearchParams] = useSearchParams();
  const consumedRef = useRef(false);
  const setCurrentChat = useAppStore((s) => s.setCurrentChat);
  const bumpFavorites = useAppStore((s) => s.bumpFavorites);
  const authReady = useAppStore((s) => s.authReady);

  useEffect(() => {
    if (consumedRef.current || !authReady) return;

    const scenario = detectEntryScenario();
    consumedRef.current = true;

    // 清掉 URL 上的入口参数，避免刷新时重复触发
    const next = new URLSearchParams(searchParams);
    next.delete("from");
    next.delete("trigger_context");
    // 保留 bdp_launch_query（SDK 需要 triggerCode）
    setSearchParams(next, { replace: true });

    handleEntry(scenario, searchParams, setCurrentChat, bumpFavorites);
  }, [authReady, searchParams, setSearchParams, setCurrentChat, bumpFavorites]);

  return null;
}

async function handleEntry(
  scenario: EntryScenario,
  searchParams: URLSearchParams,
  setCurrentChat: (chat: CurrentChatInfo) => void,
  bumpFavorites: () => void,
) {
  switch (scenario) {
    case "message_action":
      await handleMessageAction(searchParams, setCurrentChat, bumpFavorites);
      break;
    case "plus_menu":
      await handlePlusMenu(setCurrentChat);
      break;
    case "direct":
      // 无聊天上下文，不做特殊处理；发送时通过 chooseChat 选择会话
      // 清除可能残留的 pending chat（避免从 message_action 场景切换到 direct 时状态混淆）
      try { sessionStorage.removeItem(PENDING_CHAT_KEY); } catch { /* */ }
      break;
  }
}

const PENDING_CHAT_KEY = "feishu_fav_pending_chat";

/** 场景 1：消息快捷操作 — 获取消息内容并自动收藏 */
async function handleMessageAction(
  searchParams: URLSearchParams,
  setCurrentChat: (chat: CurrentChatInfo) => void,
  bumpFavorites: () => void,
) {
  const triggerRaw = searchParams.get("trigger_context");
  let fallbackMessageIds: string[] = [];
  if (triggerRaw) {
    try {
      const ctx = JSON.parse(triggerRaw);
      fallbackMessageIds = ctx.messageIds ?? [];
    } catch { /* ignore parse error */ }
  }

  const triggerCode = getTriggerCodeFromUrl();
  if (!triggerCode) {
    toast.error("未能获取 triggerCode");
    return;
  }

  try {
    // initH5Sdk 内部可能因 401 触发 OAuth 页面跳转，导致 zustand store 重置。
    // 调用前无法确定 chatId（需要 getBlockActionSourceDetail 返回），
    // 因此无法提前设置 currentChat。此处记录入口意图，以便 OAuth 回调后
    // 恢复 entry handler 的执行上下文。
    await initH5Sdk();
    const detail = await getBlockActionSourceDetail(triggerCode);
    console.info("[EntryHandler] getBlockActionSourceDetail:", JSON.stringify(detail, null, 2));

    if (!detail.support) {
      toast.error("暂不支持此消息类型");
      return;
    }

    const chatId = detail.messages?.[0]?.openChatId;

    // 先检查是否为话题群，再统一设置 currentChat（避免多次设置导致首次进入时状态不完整）
    const firstMsgId = detail.messages?.[0]?.messageId || fallbackMessageIds[0] || triggerCode;
    let isThread = false;
    let threadId: string | undefined;
    try {
      const threadCheck = await checkMessageThread(firstMsgId);
      if (threadCheck.is_thread) {
        isThread = true;
        threadId = threadCheck.thread_id;
      }
    } catch (e) {
      console.warn("[EntryHandler] checkMessageThread failed, proceed anyway:", e);
    }

    if (chatId) {
      const chatInfo: CurrentChatInfo = { chat_id: chatId, chat_type: "unknown", thread_id: threadId };
      setCurrentChat(chatInfo);
      // 保存到 sessionStorage，防止后续 saveFavoriteFromSdk 触发 401 重定向时丢失状态
      try { sessionStorage.setItem(PENDING_CHAT_KEY, JSON.stringify(chatInfo)); } catch { /* quota */ }
    }

    if (isThread) {
      toast.info("话题群消息暂不支持收藏");
      return;
    }

    if (detail.messages && detail.messages.length > 0) {
      const promises = detail.messages.map((m) =>
        saveFavoriteFromSdk({
          message_id: m.messageId,
          msg_type: m.msgType,
          content: _parseContent(m.content),
          content_text: _extractText(m.content),
          chat_id: chatId,
          sender_open_id: m.sender?.open_id,
          sender_name: m.sender?.name,
        }).catch((e) => e),
      );
      const results = await Promise.all(promises);
      const ok = results.filter((r) => r?.success === true).length;
      const fail = detail.messages!.length - ok;
      if (fail === 0) {
        toast.success(ok > 1 ? `已收藏 ${ok} 条消息` : "已收藏到知识库");
        bumpFavorites();
      } else {
        toast.error(`收藏失败 ${fail}/${detail.messages!.length}`);
      }
      return;
    }

    await saveFavoriteFromSdk({
      message_id: fallbackMessageIds[0] || triggerCode,
      msg_type: detail.msgType,
      content: _parseContent(detail.content),
      content_text: _extractText(detail.content),
      chat_id: chatId,
    });
    toast.success("已收藏到知识库");
    bumpFavorites();
  } catch (e) {
    console.error("[EntryHandler] message_action failed:", e);
    const msg = e instanceof Error ? e.message : String(e);
    toast.error(`收藏失败：${msg}`);
  }
}

/** 场景 2：+ 号菜单 — 获取 triggerContext 设置 currentChat，不自动收藏 */
async function handlePlusMenu(
  setCurrentChat: (chat: CurrentChatInfo) => void,
) {
  const triggerCode = getTriggerCodeFromUrl();
  if (!triggerCode) {
    // 没有 triggerCode 时降级为 direct 场景
    console.warn("[EntryHandler] plus_menu but no triggerCode, fallback to direct");
    return;
  }

  try {
    const ctx = await getTriggerContext(triggerCode);
    if (ctx?.openChatId) {
      setCurrentChat({ chat_id: ctx.openChatId, chat_type: "unknown" });
      console.info("[EntryHandler] plus_menu set currentChat:", ctx.openChatId);
    }
  } catch (e) {
    console.warn("[EntryHandler] getTriggerContext failed:", e);
  }
}

function _parseContent(raw: string): Record<string, unknown> {
  try {
    return JSON.parse(raw);
  } catch {
    return { text: raw };
  }
}

function _extractText(raw: string): string {
  try {
    const obj = JSON.parse(raw);
    if (obj.text) return obj.text;
    if (obj.content) return JSON.stringify(obj.content);
    return raw;
  } catch {
    return raw;
  }
}

export default function App() {
  const setMe = useAppStore((s) => s.setMe);
  const setAuthReady = useAppStore((s) => s.setAuthReady);

  useEffect(() => {
    getMe()
      .then(setMe)
      .catch(() => {
        // 401 由 apiGet 自动跳 OAuth
      })
      .finally(setAuthReady);
  }, [setMe, setAuthReady]);

  return (
    <div className="min-h-full">
      <TopNav />
      <Routes>
        <Route path="/" element={<FavoritesPage />} />
        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<UsersPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="users/:openId" element={<UserDetailPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="health" element={<HealthPage />} />
          <Route path="stats" element={<StatsPage />} />
        </Route>
        <Route path="*" element={<FavoritesPage />} />
      </Routes>
      <ToastLayer />
      <EntryHandler />
    </div>
  );
}

function TopNav() {
  const me = useAppStore((s) => s.me);
  return (
    <nav className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-2 text-sm">
        <Link to="/" className="font-semibold text-slate-900">
          我的收藏
        </Link>
        <div className="flex items-center gap-4 text-slate-600">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              isActive ? "text-slate-900" : "hover:text-slate-900"
            }
          >
            收藏
          </NavLink>
          <NavLink
            to="/groups"
            className={({ isActive }) =>
              isActive ? "text-slate-900" : "hover:text-slate-900"
            }
          >
            分组
          </NavLink>
          {me?.is_admin ? (
            <NavLink
              to="/admin"
              className="rounded-full bg-slate-900 px-3 py-1 text-xs text-white"
            >
              管理后台
            </NavLink>
          ) : null}
        </div>
      </div>
    </nav>
  );
}
